import {ErrorMessage, Field} from 'formik';
import React from 'react';

import {FormContainer} from '../../form-containers/FormGroup';
import {FormErrorMessage} from '../../form-labels/FormErrorMessage';

/**
 * @param {object} param0
 *
 * @param {string} [param0.name]
 * Meant for formik Input
 * Ensure to include the `name` property when using a Formik-type form control. Without it, the form control may not function properly.
 * Additionally, remember to wrap `<Formik></Formik>` around this and all other form control types to ensure proper functionality.
 * Adding the name to a Formik Input component is essential because Formik uses the name prop to link the value of the  textarea to the Formik state.
 * Note that the Formik type is the default version injected into the form control you are applying.
 *
 * @param {boolean} [param0.raw]
 * Meant for non formik Input (Raw)
 * If set to true, a non-Formik version of the form control will be injected instead.
 *
 * @param {boolean} [param0.code]
 * If set to true, the font in the textarea will be changed to Monospace.Do this for things like Luascript
 *
 * @param {boolean} [param0.disabled]
 * Whether the input is disabled.
 *
 * @param {boolean} [param0.readOnly]
 * If set to `true` the textarea will be read-only.
 *
 * Compared to a `disabled` field, a `readOnly` field is still focusable and its value can be selected and copied.
 * Whereas, `disabled` fields are not focusable and generally do not function as controls until they are enabled.
 *
 * @param {number | string} [param0.rows]
 * CSS variable names for adjusting the height (in rows) of the form control you're using:
 *```jsx
 *         // == --- How to use this prop:  --- ==
 *        <TextArea label="name" raw  placeholder='Type your message' rows="20" />
 * ```
 * @param {React.ReactNode} [param0.label]
 * The text to be displayed as the label.
 *
 * @param {Function} [param0.onChange]
 * Callback function to handle change events triggered by the textarea component.
 * This function will be called whenever a change event occurs in the textarea.
 *
 * @param {string} [param0.placeholder]
 * The text displayed in the Input when it is empty and not focused, providing a hint to the user about what information is expected.
 * Placeholder text follows a naming convention for consistency:
 *   - 'Add filter'
 *   - 'Type your name'
 *   - 'Select an option'
 *   - 'Pick an option'
 *
 * @param {any} [param0.value]
 * *This prop is applicable only for the non-Formik version of the control.*
 *
 * Specifies the current value of the input field.
 *
 * @param {string} [param0.validationError]
 * *This prop is applicable only for the non-Formik version of the control.*
 *
 * Error message to display when input validation fails
 */
export function TextArea({
    code,
    disabled,
    icon,
    label,
    name,
    onChange,
    raw,
    readOnly,
    rows,
    validationError,
    placeholder,
    value,
}) {
    return (
        <>
            {raw ? (
                <FormContainer
                    code={code}
                    disabled={disabled}
                    errorMessage={<>{validationError && <FormErrorMessage>{validationError}</FormErrorMessage>}</>} // Raw
                    icon={icon}
                    label={label}>
                    <textarea
                        disabled={disabled}
                        id={name}
                        name={name}
                        onChange={onChange}
                        rows={rows}
                        placeholder={placeholder}
                        readOnly={readOnly}
                        value={value}
                    />
                </FormContainer>
            ) : (
                <FormContainer
                    code={code}
                    disabled={disabled}
                    errorMessage={<ErrorMessage name={name} component={FormErrorMessage} />} // Formik
                    icon={icon}
                    label={label}>
                    <Field
                        as="textarea"
                        id={name}
                        rows={rows}
                        name={name}
                        placeholder={placeholder}
                        readOnly={readOnly}
                    />
                </FormContainer>
            )}
        </>
    );
}
